'use client';

import type { ElementType } from 'react';

import React, { forwardRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

interface ParagraphProps {
  children?: React.ReactNode;
  innerHtml?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  tag?: ElementType;
  color?: 'white' | 'muted-foreground' | 'muted' | 'black' | 'primary' | 'foreground';
  onClick?: () => void;
}

export const Paragraph = forwardRef<HTMLDivElement, ParagraphProps>(
  (
    { children, innerHtml, className, size = 'base', color = 'black', tag: Tag = 'div', onClick },
    ref,
  ) => {
    if (innerHtml) {
      return (
        <HtmlRendererRaw
          as={Tag}
          ref={ref}
          onClick={onClick}
          className={cn(
            'tracking-tight transition-all',
            'ea-paragraph__inner-html',
            `ea-paragraph__inner-${size}`,
            `ea-paragraph__inner-${color}`,
            onClick && 'cursor-pointer text-opacity-80',
            className,
          )}
          html={innerHtml}
        />
      );
    }

    return (
      <Tag
        ref={ref}
        onClick={onClick}
        className={cn(
          'ea-paragraph',
          'tracking-tight transition-all',
          size === 'xs' && 'text-[10px] sm:text-xs',
          size === 'sm' && 'text-xs sm:text-sm',
          size === 'base' && 'text-[13px] leading-[140%] sm:text-sm sm:leading-[130%]',
          size === 'lg' && 'text-sm leading-[140%] sm:text-base sm:leading-[140%]',
          size === 'xl' && 'text-lg lg:text-xl',
          size === '2xl' && 'text-lg sm:text-xl lg:text-2xl',
          color === 'white' && 'text-white',
          color === 'muted-foreground' && 'text-[#666666]',
          color === 'black' && 'text-black',
          color === 'muted' && 'text-muted',
          color === 'foreground' && 'text-foreground',
          color === 'primary' && 'text-primary',
          onClick && 'cursor-pointer text-opacity-80',
          className,
        )}
      >
        {children}
      </Tag>
    );
  },
);

Paragraph.displayName = 'Paragraph';
